import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LIMITS } from '../constants';
import type { Dictionary } from '../i18n';
import type { EditorApi } from '../state/useEditor';
import type { LoadedImage } from '../state/useImage';
import type { EditorState, Language, LayerId, Ratio, StylePreset } from '../types';
import { ExportControls, type ExportSettings } from './ExportControls';
import { FilterControls, FrameControls } from './StyleSections';
import { ImageDropZone } from './ImageDropZone';
import { PresetBar } from './PresetBar';
import { TextLayerControls } from './TextLayerControls';

interface Props {
  t: Dictionary;
  language: Language;
  editor: EditorApi;
  image: LoadedImage | null;
  imageLoading: boolean;
  onFile: (file: File) => void;
  onClearImage: () => void;
  presets: StylePreset[];
  onApplyPreset: (preset: StylePreset) => void;
  onSavePreset: (name: string) => void;
  onDeletePreset: (id: string) => void;
  exportSettings: ExportSettings;
  onExportSettings: (patch: Partial<ExportSettings>) => void;
  onDownload: () => void;
  onCopy: () => void;
  exporting: boolean;
  expanded: string | false;
  onExpanded: (panel: string | false) => void;
  onFocusLayer: (id: LayerId) => void;
}

export function ControlPanel(props: Props) {
  const { t, editor, expanded, onExpanded } = props;
  const { state } = editor;

  const section = (id: string, title: string, children: React.ReactNode) => (
    <Accordion expanded={expanded === id} onChange={(_, open) => onExpanded(open ? id : false)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${id}-content`} id={`${id}-header`}>
        <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails id={`${id}-content`}>{children}</AccordionDetails>
    </Accordion>
  );

  return (
    <Stack spacing={2}>
      <ImageDropZone
        t={t}
        image={props.image}
        isLoading={props.imageLoading}
        onFile={props.onFile}
        onClear={props.onClearImage}
      />

      <ToggleButtonGroup
        exclusive
        fullWidth
        value={state.ratio}
        onChange={(_, value: Ratio | null) => value !== null && editor.setRatio(value)}
        aria-label={t.ratio}
      >
        <ToggleButton value="16:9">{t.ratio169}</ToggleButton>
        <ToggleButton value="9:16">{t.ratio916}</ToggleButton>
      </ToggleButtonGroup>

      <TextField
        fullWidth
        multiline
        maxRows={3}
        label={t.mainTitle}
        placeholder={t.mainTitlePlaceholder}
        value={state.main.text}
        onFocus={() => props.onFocusLayer('main')}
        onChange={(event) => editor.setLayer('main', { text: event.target.value }, { tag: 'mainText' })}
        slotProps={{ htmlInput: { maxLength: LIMITS.mainTextMaxLength } }}
        helperText={t.multilineHint}
      />
      <TextField
        fullWidth
        multiline
        maxRows={3}
        label={t.subTitle}
        placeholder={t.subTitlePlaceholder}
        value={state.sub.text}
        onFocus={() => props.onFocusLayer('sub')}
        onChange={(event) => editor.setLayer('sub', { text: event.target.value }, { tag: 'subText' })}
        slotProps={{ htmlInput: { maxLength: LIMITS.subTextMaxLength } }}
      />

      {section(
        'presets',
        `${t.templates} / ${t.presets}`,
        <PresetBar
          t={t}
          language={props.language}
          onApplyTemplate={(patch: Partial<EditorState>) => editor.applyPatch(patch)}
          presets={props.presets}
          onApplyPreset={props.onApplyPreset}
          onSavePreset={props.onSavePreset}
          onDeletePreset={props.onDeletePreset}
        />,
      )}

      {section(
        'main',
        `${t.mainTitle} · ${t.fontStyle}`,
        <TextLayerControls
          t={t}
          layer={state.main}
          onChange={(patch, tag) => editor.setLayer('main', patch, tag ? { tag: `main:${tag}` } : undefined)}
        />,
      )}

      {section(
        'sub',
        `${t.subTitle} · ${t.fontStyle}`,
        <TextLayerControls
          t={t}
          layer={state.sub}
          onChange={(patch, tag) => editor.setLayer('sub', patch, tag ? { tag: `sub:${tag}` } : undefined)}
        />,
      )}

      {section(
        'filter',
        t.imageFilter,
        <FilterControls
          t={t}
          filters={state.filters}
          backgroundColor={state.backgroundColor}
          onChange={(patch, tag) => editor.setFilters(patch, tag ? { tag } : undefined)}
          onBackgroundColor={(value) =>
            editor.update((prev) => ({ ...prev, backgroundColor: value }), { tag: 'canvasBg' })
          }
        />,
      )}

      {section(
        'frame',
        t.frame,
        <FrameControls
          t={t}
          frame={state.frame}
          onChange={(patch, tag) => editor.setFrame(patch, tag ? { tag } : undefined)}
        />,
      )}

      {section(
        'export',
        t.exportSection,
        <ExportControls
          t={t}
          ratio={state.ratio}
          settings={props.exportSettings}
          onChange={props.onExportSettings}
          onDownload={props.onDownload}
          onCopy={props.onCopy}
          busy={props.exporting}
          disabled={props.image === null}
        />,
      )}
    </Stack>
  );
}
